import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Phone, Mail, Pencil, Trash2, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

export default function AdminOwners() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchOwners = useCallback(async () => {
    try {
      const response = await api.get('/owners');
      const data = response.data || [];

      const filtered = search
        ? data.filter((owner) =>
            [
              owner.name,
              owner.email,
              owner.phone,
              owner.mobile,
              owner.cpf,
              owner.rg,
            ]
              .filter(Boolean)
              .some((value) =>
                String(value).toLowerCase().includes(search.toLowerCase())
              )
          )
        : data;

      setOwners(filtered);
    } catch (error) {
      console.error('Error fetching owners:', error);
      toast.error('Erro ao carregar proprietários.');
    } finally {
      setLoading(false);
    }
  }, [api, search]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchOwners();
    }, 300);

    return () => clearTimeout(debounce);
  }, [fetchOwners]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/owners/${id}`);
      toast.success('Proprietário removido com sucesso.');
      fetchOwners();
    } catch (error) {
      toast.error('Erro ao remover proprietário.');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-owners">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">
            Proprietários
          </h1>
          <p className="text-slate-500 mt-1">
            {owners.length} proprietários cadastrados
          </p>
        </div>

        <Button asChild className="bg-brand-blue hover:bg-brand-blue-dark">
          <Link to="/admin/owners/new">
            <Plus className="w-4 h-4 mr-2" />
            Novo Proprietário
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome, CPF, RG, telefone ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
        </div>
      ) : owners.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-slate-500 mb-4">Nenhum proprietário encontrado.</p>
            <Button asChild>
              <Link to="/admin/owners/new">
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Primeiro Proprietário
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {owners.map((owner) => (
            <Card key={owner.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-blue/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-brand-blue font-semibold text-lg">
                      {owner.name?.charAt(0)?.toUpperCase() || 'P'}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-lg text-slate-800">
                        {owner.name}
                      </h3>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      {owner.mobile && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {owner.mobile}
                        </span>
                      )}
                      {owner.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {owner.email}
                        </span>
                      )}
                      {owner.property_ids?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {owner.property_ids.length} imóvel(is)
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-slate-500 mt-2">
                      {owner.cpf && <span>CPF: {owner.cpf}</span>}
                      {owner.rg && <span>RG: {owner.rg}</span>}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => navigate(`/admin/owners/${owner.id}`)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon" className="text-red-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tem certeza que deseja remover este proprietário? Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(owner.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}